import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, Info, Layers, Plus, Star, X } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  useAddEntityToWatchlist,
  useCreateWatchlist,
  useCreateRollupFoundation,
  useEntityWatchlists,
  useRemoveEntityFromWatchlist,
  useRollupFoundations,
  useWatchlists,
} from '../api/hooks.js';
import { useOpenCompanyOverview } from './use-open-company-overview.js';

// Right-click action sheet for entity rows. Wraps any element and
// intercepts the contextmenu event to render a small menu at the
// cursor position. Closes on click-away, Escape, or after picking
// an action.
//
// Actions (current spec):
//   Company information → opens a Company Overview tab focused on this
//                         entity in the active workspace tab. Reuses an
//                         existing Company Overview if one is already
//                         open in the tab.
//   Add to watchlist    → submenu listing the user's named watchlists.
//                         Check marks indicate current membership.
//                         "Default" is always present — picks the first
//                         existing watchlist or auto-creates one named
//                         "Default" if none exist.
//                         "+ Create new watchlist…" inline input adds to
//                         a brand-new list.

export interface EntityRowContextMenuProps {
  entityId: string;
  legalName: string | null;
  /** If the row is being rendered inside a watchlist panel, pass the
   *  watchlist's id here. The submenu then reads as "Move to another
   *  watchlist" and clicking a destination MOVES the entity
   *  (removes from `inWatchlistId`, adds to the chosen list) rather
   *  than copying it onto multiple lists. Omit on Screener / generic
   *  rows — the default "Add to watchlist" behaviour applies. */
  inWatchlistId?: string;
  children: ReactNode;
}

interface MenuState {
  open: boolean;
  x: number;
  y: number;
}

export function EntityRowContextMenu({
  entityId,
  legalName,
  inWatchlistId,
  children,
}: EntityRowContextMenuProps) {
  const { t } = useTranslation();
  const [menu, setMenu] = useState<MenuState>({ open: false, x: 0, y: 0 });
  const [showWatchlistPicker, setShowWatchlistPicker] = useState(false);
  const [showFoundationPicker, setShowFoundationPicker] = useState(false);
  const [naming, setNaming] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [namingFoundation, setNamingFoundation] = useState(false);
  const [draftFoundationName, setDraftFoundationName] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const addMut = useAddEntityToWatchlist();
  const removeMut = useRemoveEntityFromWatchlist();
  const createMut = useCreateWatchlist();
  const createFoundationMut = useCreateRollupFoundation();
  const { data: watchlistsData } = useWatchlists();
  const { data: foundationsData } = useRollupFoundations();
  const { data: entityWatchlistsData } = useEntityWatchlists(menu.open ? entityId : null);
  const openCompanyOverview = useOpenCompanyOverview();
  const watchlists = watchlistsData?.data ?? [];
  const foundations = foundationsData?.data ?? [];
  const onSet = useMemo(
    () => new Set((entityWatchlistsData?.data ?? []).map((w) => w.watchlistId)),
    [entityWatchlistsData],
  );

  function close() {
    setMenu({ open: false, x: 0, y: 0 });
    setShowWatchlistPicker(false);
    setShowFoundationPicker(false);
    setNaming(false);
    setDraftName('');
    setNamingFoundation(false);
    setDraftFoundationName('');
  }
  function toggleWatchlist(watchlistId: string) {
    if (onSet.has(watchlistId)) {
      removeMut.mutate({ watchlistId, entityId });
    } else {
      addMut.mutate({ watchlistId, entityId });
    }
  }
  // Move-to-watchlist semantics (only used when `inWatchlistId` is set,
  // i.e. the row lives inside a watchlist panel). Adds the entity to
  // the chosen destination, then removes it from the source list. The
  // remove-after-add ordering keeps the entity persistently linked
  // even if the network round-trip is slow.
  function moveToWatchlist(destinationWatchlistId: string) {
    if (!inWatchlistId || destinationWatchlistId === inWatchlistId) return;
    addMut.mutate(
      { watchlistId: destinationWatchlistId, entityId },
      {
        onSuccess: () => removeMut.mutate({ watchlistId: inWatchlistId, entityId }),
      },
    );
  }
  function openCompanyInfo() {
    openCompanyOverview(entityId);
    close();
  }
  function removeFromCurrentWatchlist() {
    if (!inWatchlistId) return;
    removeMut.mutate({ watchlistId: inWatchlistId, entityId });
    close();
  }
  function commitNewWatchlist() {
    const name = draftName.trim();
    if (!name) return;
    createMut.mutate(name, {
      onSuccess: ({ data }) => {
        addMut.mutate({ watchlistId: data.id, entityId });
        setNaming(false);
        setDraftName('');
      },
    });
  }
  // Roll-Up Foundations are watchlists (kind 'rollup'), so add/remove reuse the
  // same membership mutations — toggleWatchlist works for a foundation id too.
  function commitNewFoundation() {
    const name = draftFoundationName.trim();
    if (!name) return;
    createFoundationMut.mutate(name, {
      onSuccess: ({ data }) => {
        addMut.mutate({ watchlistId: data.id, entityId });
        setNamingFoundation(false);
        setDraftFoundationName('');
      },
    });
  }

  useEffect(() => {
    if (!menu.open) return undefined;
    function onMouseDown(e: MouseEvent) {
      if (ref.current && ref.current.contains(e.target as Node)) return;
      close();
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [menu.open]);

  function onContext(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ open: true, x: e.clientX, y: e.clientY });
    setShowWatchlistPicker(false);
    setNaming(false);
  }

  return (
    <>
      <div onContextMenu={onContext} style={{ display: 'contents' }}>
        {children}
      </div>
      {menu.open &&
        createPortal(
          <div
            ref={ref}
            role="menu"
            aria-label={legalName ?? entityId}
            style={{
              position: 'fixed',
              top: menu.y,
              left: menu.x,
              minWidth: '220px',
              backgroundColor: 'var(--menu-surface)',
              border: '1px solid var(--hairline)',
              borderRadius: '2px',
              boxShadow: '0 6px 24px rgba(10,14,26,0.14), 0 2px 6px rgba(10,14,26,0.08)',
              zIndex: 9999,
              padding: 'var(--s-1)',
              fontSize: 'var(--t-body)',
            }}
          >
            <Item icon={<Info size={12} />} onClick={openCompanyInfo}>
              {t('entityContext.companyInformation')}
            </Item>
            <Item
              icon={<Star size={12} />}
              trailing={<ChevronRight size={12} />}
              onClick={() => setShowWatchlistPicker((v) => !v)}
            >
              {inWatchlistId
                ? t('entityContext.moveToAnotherWatchlist')
                : t('entityContext.addToWatchlist')}
            </Item>
            {showWatchlistPicker && (
              <div
                style={{
                  borderTop: '1px solid var(--hairline)',
                  marginTop: 'var(--s-1)',
                  paddingTop: 'var(--s-1)',
                }}
              >
                {/* Show every watchlist by its real name so this list is
                    identical to the Watchlist panel's selector dropdown.
                    (Previously the first list was masked as a synthetic
                    "Default" entry, which made the two lists disagree and
                    sent "add to Default" to an unexpected list.) When the
                    row lives inside a watchlist panel, the current list is
                    omitted — you can only MOVE to a different one. */}
                {watchlists
                  .filter((w) => w.id !== inWatchlistId)
                  .map((w) => (
                    <Item
                      key={w.id}
                      icon={
                        !inWatchlistId && onSet.has(w.id) ? (
                          <Check size={12} color="var(--accent)" />
                        ) : (
                          <span style={{ width: 12 }} />
                        )
                      }
                      onClick={() =>
                        inWatchlistId ? moveToWatchlist(w.id) : toggleWatchlist(w.id)
                      }
                    >
                      {w.name}
                    </Item>
                  ))}
                <div
                  style={{
                    borderTop: '1px solid var(--hairline)',
                    marginTop: 'var(--s-1)',
                    paddingTop: 'var(--s-1)',
                  }}
                >
                  {naming ? (
                    <div style={{ display: 'flex', gap: 4, padding: '2px 4px' }}>
                      <input
                        autoFocus
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        placeholder={t('entityContext.newWatchlistPlaceholder')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitNewWatchlist();
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            setNaming(false);
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: '2px 4px',
                          border: '1px solid var(--accent)',
                          borderRadius: 2,
                          background: 'var(--surface)',
                          color: 'var(--text-primary)',
                          fontSize: 'var(--t-body)',
                        }}
                      />
                      <button
                        type="button"
                        onClick={commitNewWatchlist}
                        disabled={!draftName.trim() || createMut.isPending}
                        style={{
                          padding: '2px 8px',
                          background: 'var(--accent)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 2,
                          fontSize: 'var(--t-meta)',
                          cursor: 'pointer',
                        }}
                      >
                        {t('entityContext.create')}
                      </button>
                    </div>
                  ) : (
                    <Item icon={<Plus size={12} />} onClick={() => setNaming(true)}>
                      {t('entityContext.createNewWatchlist')}
                    </Item>
                  )}
                </div>
              </div>
            )}
            <Item
              icon={<Layers size={12} />}
              trailing={<ChevronRight size={12} />}
              onClick={() => setShowFoundationPicker((v) => !v)}
            >
              Add to roll-up foundation
            </Item>
            {showFoundationPicker && (
              <div
                style={{
                  borderTop: '1px solid var(--hairline)',
                  marginTop: 'var(--s-1)',
                  paddingTop: 'var(--s-1)',
                }}
              >
                {foundations.length === 0 && !namingFoundation && (
                  <div
                    style={{
                      padding: '4px 8px',
                      color: 'var(--text-tertiary)',
                      fontSize: 'var(--t-meta)',
                    }}
                  >
                    No roll-up foundations yet
                  </div>
                )}
                {/* Foundations are watchlists, so the same membership toggle
                    applies — adding here keeps the entity wherever else it lives
                    (a buy-list is additive, not a move). */}
                {foundations.map((w) => (
                  <Item
                    key={w.id}
                    icon={
                      onSet.has(w.id) ? (
                        <Check size={12} color="var(--accent)" />
                      ) : (
                        <span style={{ width: 12 }} />
                      )
                    }
                    onClick={() => toggleWatchlist(w.id)}
                  >
                    {w.name}
                  </Item>
                ))}
                <div
                  style={{
                    borderTop: '1px solid var(--hairline)',
                    marginTop: 'var(--s-1)',
                    paddingTop: 'var(--s-1)',
                  }}
                >
                  {namingFoundation ? (
                    <div style={{ display: 'flex', gap: 4, padding: '2px 4px' }}>
                      <input
                        autoFocus
                        value={draftFoundationName}
                        onChange={(e) => setDraftFoundationName(e.target.value)}
                        placeholder="New foundation name"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitNewFoundation();
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            setNamingFoundation(false);
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: '2px 4px',
                          border: '1px solid var(--accent)',
                          borderRadius: 2,
                          background: 'var(--surface)',
                          color: 'var(--text-primary)',
                          fontSize: 'var(--t-body)',
                        }}
                      />
                      <button
                        type="button"
                        onClick={commitNewFoundation}
                        disabled={!draftFoundationName.trim() || createFoundationMut.isPending}
                        style={{
                          padding: '2px 8px',
                          background: 'var(--accent)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 2,
                          fontSize: 'var(--t-meta)',
                          cursor: 'pointer',
                        }}
                      >
                        {t('entityContext.create')}
                      </button>
                    </div>
                  ) : (
                    <Item icon={<Plus size={12} />} onClick={() => setNamingFoundation(true)}>
                      Create new foundation…
                    </Item>
                  )}
                </div>
              </div>
            )}
            {inWatchlistId && (
              <div
                style={{
                  borderTop: '1px solid var(--hairline)',
                  marginTop: 'var(--s-1)',
                  paddingTop: 'var(--s-1)',
                }}
              >
                <Item
                  icon={<X size={12} color="var(--color-negative-500)" />}
                  onClick={removeFromCurrentWatchlist}
                >
                  {t('entityContext.removeFromWatchlist')}
                </Item>
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

function Item({
  icon,
  trailing,
  onClick,
  children,
}: {
  icon: ReactNode | null;
  trailing?: ReactNode;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      role="menuitem"
      type="button"
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        width: '100%',
        alignItems: 'center',
        gap: 'var(--s-2)',
        padding: '4px 8px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--text-primary)',
        fontSize: 'inherit',
        textAlign: 'left',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--hover-subtle)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {icon ?? <span style={{ width: 12 }} />}
      <span>{children}</span>
      <span style={{ color: 'var(--text-tertiary)' }}>{trailing}</span>
    </button>
  );
}
